import {useCallback, useEffect, useRef, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Snackbar,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {getPickupBoardAPI, markPickupAPI} from "../../utils/ordersControlar";

// 取餐叫號：電視看板（宅食光門口）上的號碼。請取餐的號碼點一下即標記已取餐、立刻從電視下架，
// 誤按可在提示列按「復原」。後端：ciaowin PosPickupController（GET /pickup_board、POST /pickup/{id}）
export const PICKUP_REFRESH_MS = 5000;
const UNDO_HIDE_MS = 6000;

const sectionTitleSx = {fontSize: 20, fontWeight: 900, color: "#202124"};

export default function PickupBoardDialog({open, onClose}) {
    const [board, setBoard] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [notice, setNotice] = useState(null); // {message, severity, undo?: {order_id, no}}
    const [noticeOpen, setNoticeOpen] = useState(false);
    // 送出中的單先藏起來，避免進行中的輪詢把剛按掉的號碼又帶回來
    const hiddenIds = useRef(new Set());
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const refresh = useCallback(async () => {
        let data = null;
        try {
            data = await getPickupBoardAPI({silent: true});
        } catch (e) {
            data = null;
        }
        if (!mounted.current) return;
        if (data) {
            setBoard(data);
            setLoadFailed(false);
        } else {
            setLoadFailed(true);
        }
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        refresh();
        const timerId = setInterval(refresh, PICKUP_REFRESH_MS);
        window.addEventListener("pickup-board-updated", refresh);
        return () => {
            clearInterval(timerId);
            window.removeEventListener("pickup-board-updated", refresh);
        };
    }, [open, refresh]);

    useEffect(() => {
        if (!open) setNoticeOpen(false);
    }, [open]);

    const showNotice = (next) => {
        setNotice(next);
        setNoticeOpen(true);
    };

    const submit = async (item, undo) => {
        let res;
        try {
            res = await markPickupAPI(item.order_id, {undo, silent: true});
        } catch (e) {
            res = {error: 1, message: e?.response?.data?.message};
        }
        return res && res.error === 0 ? null : (res?.message || "伺服器錯誤");
    };

    const handlePicked = async (item) => {
        hiddenIds.current.add(item.order_id);
        setBoard((prev) => prev && {...prev, ready: prev.ready.filter((r) => r.order_id !== item.order_id)});
        const error = await submit(item, false);
        hiddenIds.current.delete(item.order_id);
        if (!mounted.current) return;
        showNotice(error
            ? {message: `${item.no} 號標記失敗：${error}`, severity: "error"}
            : {message: `${item.no} 號已取餐`, severity: "success", undo: item});
        refresh();
    };

    const handleUndo = async () => {
        const item = notice?.undo;
        if (!item) return;
        setNoticeOpen(false);
        setNotice(null);
        const error = await submit(item, true);
        if (!mounted.current) return;
        showNotice(error
            ? {message: `${item.no} 號復原失敗：${error}`, severity: "error"}
            : {message: `已復原 ${item.no} 號`, severity: "info"});
        refresh();
    };

    const ready = (board?.ready || []).filter((r) => !hiddenIds.current.has(r.order_id));
    const making = board?.making || [];
    const makingMore = Math.max(0, (board?.making_total || 0) - making.length);
    const wait = board?.wait;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            slotProps={{paper: {sx: {borderRadius: "16px"}}}}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                    fontSize: 26,
                    fontWeight: 900,
                    color: "#1C2D42",
                    pr: 7,
                }}
            >
                取餐叫號
                {wait ? (
                    <Box
                        component="span"
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "999px",
                            fontSize: 16,
                            fontWeight: 900,
                            color: "#92400E",
                            backgroundColor: "#FFF7ED",
                            border: "1px solid #FDBA74",
                        }}
                    >
                        預計等候 {wait.minutes} 分鐘
                    </Box>
                ) : null}
                <IconButton
                    aria-label="關閉取餐叫號"
                    onClick={onClose}
                    sx={{position: "absolute", right: 12, top: 12}}
                >
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{pb: 3}}>
                {loadFailed ? (
                    <Alert severity="warning" sx={{mb: 2, fontWeight: 800}}>
                        無法取得取餐叫號資料，{PICKUP_REFRESH_MS / 1000} 秒後自動重試
                    </Alert>
                ) : null}

                <Box sx={{display: "flex", alignItems: "baseline", gap: 1, mb: 1.5}}>
                    <Typography sx={{...sectionTitleSx, color: "#15803D"}}>請取餐</Typography>
                    <Typography sx={{fontSize: 14, fontWeight: 800, color: "#536073"}}>
                        點號碼即標記已取餐，電視立即下架
                    </Typography>
                </Box>
                {board && ready.length === 0 ? (
                    <Typography sx={{color: "#6B7280", fontWeight: 800, mb: 3}}>目前沒有待取餐號碼</Typography>
                ) : (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))",
                            gap: 1.5,
                            mb: 3,
                        }}
                    >
                        {ready.map((item) => (
                            <Button
                                key={item.order_id}
                                aria-label={`${item.no} 號已取餐`}
                                onClick={() => handlePicked(item)}
                                variant={item.fresh ? "contained" : "outlined"}
                                sx={{
                                    minHeight: 88,
                                    borderRadius: "14px",
                                    borderWidth: 2,
                                    fontSize: 40,
                                    fontWeight: 900,
                                    lineHeight: 1,
                                    borderColor: "#15803D",
                                    color: item.fresh ? "#FFFFFF" : "#166534",
                                    backgroundColor: item.fresh ? "#15803D" : "#FFFFFF",
                                    boxShadow: "none",
                                    "&:hover": {
                                        borderWidth: 2,
                                        borderColor: "#166534",
                                        backgroundColor: item.fresh ? "#166534" : "#F0FDF4",
                                        boxShadow: "none",
                                    },
                                }}
                            >
                                {item.no}
                            </Button>
                        ))}
                    </Box>
                )}

                <Typography sx={{...sectionTitleSx, color: "#B45309", mb: 1}}>製作中</Typography>
                {board && making.length === 0 ? (
                    <Typography sx={{color: "#6B7280", fontWeight: 800}}>目前沒有製作中的訂單</Typography>
                ) : (
                    <Box data-testid="pickup-making" sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
                        {making.map((item) => (
                            <Box
                                key={item.order_id}
                                sx={{
                                    minWidth: 64,
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: "10px",
                                    textAlign: "center",
                                    fontSize: 22,
                                    fontWeight: 900,
                                    color: "#92400E",
                                    backgroundColor: "#FFF7ED",
                                    border: "1px solid #FED7AA",
                                }}
                            >
                                {item.no}
                            </Box>
                        ))}
                        {makingMore > 0 ? (
                            <Typography sx={{alignSelf: "center", color: "#536073", fontWeight: 800}}>
                                另有 {makingMore} 單
                            </Typography>
                        ) : null}
                    </Box>
                )}
            </DialogContent>
            <Snackbar
                open={noticeOpen && Boolean(notice)}
                autoHideDuration={UNDO_HIDE_MS}
                onClose={(e, reason) => { if (reason !== "clickaway") setNoticeOpen(false); }}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            >
                {notice ? (
                    <Alert
                        severity={notice.severity}
                        variant="filled"
                        sx={{fontSize: 18, fontWeight: 900, alignItems: "center"}}
                        action={notice.undo ? (
                            <Button color="inherit" onClick={handleUndo} sx={{fontSize: 18, fontWeight: 900}}>
                                復原
                            </Button>
                        ) : null}
                    >
                        {notice.message}
                    </Alert>
                ) : <span/>}
            </Snackbar>
        </Dialog>
    );
}
